import { UseMutationResult, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Image from "next/image";
import Link from "next/link";
import { useState } from "react";

import { fetchBannerList, reOrderBanner } from "../../core/banner.core";
import { useTranslation } from "../../core/translation.core";
import { Banner } from "../../types/banner";
import LocalePs from "../locale/localePs.core";
import { ListSection, Tag } from "../reuseables/list.comp";

export default function BannerList() {
  const { t, msg } = useTranslation();

  return (
    <>
      <p>{t(msg.banner.dragInstruction)}</p>
      <div className="space-y-10">
        <BannerSection groupName="topBar" title={t(msg.banner.topBarBanners)} />
        <BannerSection groupName="header" title={t(msg.banner.headerBanners)} />
        <BannerSection groupName="carousel" title={t(msg.banner.carouselBanners)} />
        <BannerSection groupName="section" title={t(msg.banner.sectionBanners)} />
        <BannerSection groupName="sidebar" title={t(msg.banner.sidebarBanners)} />
        <BannerSection groupName="footer" title={t(msg.banner.footerBanners)} />
      </div>
    </>
  );
}

interface ReorderParams {
  bannerId: string;
  newOrder: number;
}

const BannerSection = ({ groupName, title }: { groupName: string; title: string }) => {
  const { t, msg } = useTranslation();
  const { data, isFetched } = useQuery({
    queryKey: ["bannerList", groupName],
    queryFn: (context) => fetchBannerList(context.queryKey[1]),
  });
  const queryClient = useQueryClient();

  const reOrderMutation = useMutation({
    mutationFn: async ({ bannerId, newOrder }: ReorderParams) => {
      const sortedBannerIds = data!
        .filter((d) => d.id !== bannerId)
        .sort((a, b) => a.order - b.order)
        .map((d) => d.id);

      sortedBannerIds.splice(newOrder, 0, bannerId);

      await reOrderBanner(sortedBannerIds);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bannerList"] });
    },
  });

  if (!isFetched) return null;
  if (!data) return null;

  return (
    <div>
      <h2 className="mb-4">{title}</h2>
      <ListSection id={`banner-section-${groupName}`}>
        <div id="product-section" className="pt-6 lg:pt-0">
          <div className="hidden lg:grid lg:grid-cols-[10px_100px_200px_auto_100px] gap-6 px-6 py-3 text-sm font-medium ">
            <div></div>
            <div>{t(msg.media.media)}</div>
            <div>{t(msg.general.name)}</div>
            <div>{t(msg.general.url)}</div>
            <div></div>
          </div>

          {data
            ?.sort((a, b) => a?.order - b.order)
            ?.map((banner, idx) => (
              <BannerItem key={banner.id} banner={banner} idx={idx} reOrder={reOrderMutation} />
            ))}

          {data.length > 0 && <DropDiv dragIdx={data.length} reOrder={reOrderMutation} groupName={groupName} />}
        </div>
      </ListSection>
    </div>
  );
};

interface DropDivProps {
  dragIdx: number;
  reOrder: UseMutationResult<void, unknown, ReorderParams, unknown>;
  groupName: string;
}

const DropDiv = ({ dragIdx, reOrder, groupName }: DropDivProps) => {
  const [onDrag, setOnDrag] = useState(false);

  function drop(ev: React.DragEvent<HTMLDivElement>) {
    setOnDrag(false);
    ev.preventDefault();
    if (allowDrag()) {
      const bannerId = ev.dataTransfer.getData("id");
      reOrder.mutateAsync({ bannerId, newOrder: dragIdx });
    }

    (window as any).draggingBanner = null;
  }

  function onDragOver(ev: React.DragEvent<HTMLDivElement>) {
    ev.preventDefault();
    setOnDrag(allowDrag());
  }

  function allowDrag() {
    const { banner, srcIdx }: { banner: Banner; srcIdx: number } = (window as any).draggingBanner as any;

    if (banner.groupName !== groupName) return false;
    if (srcIdx === dragIdx || srcIdx === dragIdx - 1) return false;

    return true;
  }

  return (
    <div onDrop={drop} onDragLeave={() => setOnDrag(false)} onDragOver={onDragOver} className="p-2">
      {onDrag && <div className="h-[50px] border border-dashed rounded-lg bg-gray-400 dark:bg-gray-500"></div>}
    </div>
  );
};

interface BannerItemProps {
  banner: Banner;
  idx: number;
  reOrder: UseMutationResult<void, unknown, ReorderParams, unknown>;
}

const BannerItem = ({ banner, idx, reOrder }: BannerItemProps) => {
  const { t, msg } = useTranslation();

  function drag(ev: React.DragEvent<HTMLDivElement>) {
    ev.dataTransfer.setData("id", banner.id);
    ev.dataTransfer.setData("idx", idx.toString());
    ev.dataTransfer.setData("groupName", banner.groupName);
    (window as any).draggingBanner = { banner, srcIdx: idx };
  }

  return (
    <>
      <DropDiv dragIdx={idx} reOrder={reOrder} groupName={banner.groupName} />
      <div
        className="grid grid-cols-[10px_100px_auto] lg:grid-cols-[10px_100px_200px_auto_100px] gap-x-6 gap-y-3 px-6 py-6 border-t-[1px] text-sm cursor-pointer group/edit"
        draggable="true"
        onDragStart={drag}
      >
        <div className="row-span-3">#{idx + 1}</div>
        <div className="row-span-3">
          {banner?.media?.filename && (
            <Image unoptimized src={banner?.media?.thumbnailUrl} width="100" height="100" alt={banner?.media?.filename} />
          )}
        </div>
        <div>
          <LocalePs value={banner.html} />
        </div>
        <div>
          {banner.href && (
            <>
              <Tag>{t(msg.general.url)}</Tag>
              <Link href={banner.href} className="underline" target="_blank">
                {banner.href}
              </Link>
            </>
          )}
        </div>
        <Link href={`/banner/edit/${banner.id}`} className="cursor-pointer opacity-0 group-hover/edit:opacity-100 transition-all">
          {t(msg.form.edit)}
        </Link>
      </div>
    </>
  );
};
